import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';

import { Shield, Users, Clock, AlertTriangle, CheckCircle, XCircle, RefreshCw, Send, Loader2, Mail, FileText, DollarSign, Download, FileSpreadsheet, Award } from 'lucide-react';

import DocumentRectificationModal from '../DocumentRectificationModal';
import { useRcdsSouscriptions } from './useRcdsSouscriptions';
import { CompteursDispositif } from './CompteursDispositif';
import { EvolutionDispositifs } from './EvolutionDispositifs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { OverviewTab, ExpiringTab } from './RcdsTabsA';
import { AttestationsTab, RappelsTab } from './RcdsTabsB';

export default function RCDSSouscriptionsDashboard(props) {
  const ctx = useRcdsSouscriptions(props);
  const { exportDispositif, setExportDispositif, activeTab, attTotal, _attestations, expiring, exportLoading, fetchExpiring, fetchList, fetchStats, getAuthHeader, handleExportCSV, handleExportExcel, handleTriggerAll, isAdmin, isSuperAdmin, rectifyModal, stats, total, triggeringAll, setRectifyModal, setActiveTab } = ctx;
  return (
    <div className="space-y-6" data-testid="rcds-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-blue-600" />
            Suivi Adhesions RCDS
          </h2>
          <p className="text-sm text-gray-500 mt-1">Responsabilite Civile et Decennale Solidaire</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Select value={exportDispositif} onValueChange={setExportDispositif}>
                <SelectTrigger className="h-9 w-60" data-testid="export-dispositif-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Export : tous les dispositifs</SelectItem>
                  <SelectItem value="annuel">Export : Annuelle</SelectItem>
                  <SelectItem value="nominative">Export : Nominative</SelectItem>
                  <SelectItem value="archos">Export : ARCHOS</SelectItem>
                  <SelectItem value="avenant">Export : Avenant</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={exportLoading === 'csv'} data-testid="export-csv-btn">
                {exportLoading === 'csv' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={exportLoading === 'excel'} data-testid="export-excel-btn">
                {exportLoading === 'excel' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
                Excel
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => { fetchStats(); fetchList(); fetchExpiring(); }} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={handleTriggerAll} disabled={triggeringAll} className="bg-blue-600 hover:bg-blue-700" data-testid="trigger-all-btn">
              {triggeringAll ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Envoyer rappels
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stats-cards">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white">
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs opacity-80">Total</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-green-600 to-green-700 text-white">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{stats.actives}</p>
              <p className="text-xs opacity-80">Actives</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4 text-center">
              <Clock className="h-6 w-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{stats.en_attente_paiement}</p>
              <p className="text-xs opacity-80">Att. paiement</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-500 to-red-500 text-white">
            <CardContent className="p-4 text-center">
              <AlertTriangle className="h-6 w-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{stats.expirant_30j}</p>
              <p className="text-xs opacity-80">Expirant 30j</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-red-600 to-red-700 text-white">
            <CardContent className="p-4 text-center">
              <XCircle className="h-6 w-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{stats.expirees}</p>
              <p className="text-xs opacity-80">Expirees</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-indigo-600 to-indigo-700 text-white">
            <CardContent className="p-4 text-center">
              <DollarSign className="h-6 w-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{(stats.montant_encaisse || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</p>
              <p className="text-xs opacity-80">Encaisse (EUR)</p>
            </CardContent>
          </Card>
        </div>
      )}
      <CompteursDispositif parDispositif={stats?.par_dispositif} />
      <EvolutionDispositifs getAuthHeader={getAuthHeader} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="overview" className="flex items-center gap-1.5">
            <FileText className="h-4 w-4" /> Adhesions
            <Badge className="bg-blue-600 text-white text-xs ml-1 font-semibold">{total}</Badge>
          </TabsTrigger>
          <TabsTrigger value="expiring" className="flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> Expirations
            {expiring.length > 0 && <Badge className="bg-orange-600 text-white text-xs ml-1 font-semibold">{expiring.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="attestations" className="flex items-center gap-1.5">
            <Award className="h-4 w-4" /> Attestations signees
            {attTotal > 0 && <Badge className="bg-emerald-600 text-white text-xs ml-1 font-semibold">{attTotal}</Badge>}
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="rappels" className="flex items-center gap-1.5">
              <Mail className="h-4 w-4" /> Rappels
            </TabsTrigger>
          )}
        </TabsList>
        <OverviewTab ctx={ctx} />
        <ExpiringTab ctx={ctx} />
        <AttestationsTab ctx={ctx} />
        {isAdmin && <RappelsTab ctx={ctx} />}
      </Tabs>

      {/* Modale Rectification document RCDS signe (Super Admin only) */}
      {isSuperAdmin && (
        <DocumentRectificationModal
          open={rectifyModal.open}
          onClose={() => setRectifyModal({ open: false, souscription_id: null })}
          reference={rectifyModal.souscription_id}
          getAuthHeader={getAuthHeader}
          domain="rcds"
          defaultDocumentType="rcds_attestation"
        />
      )}
    </div>
  );
}
